/* eslint-disable react/prop-types */
/**
 * Active Care payment link (Standard, Flexible or Drop-in Care), raised from
 * the Booking screen instead of a Finance ticket on the issue tracker.
 *
 * Pick the parent and child and the plan details; the backend prices it,
 * raises the invoice and the Razorpay link. The link is shown with a copy
 * button so the coordinator can send it to the parent. Standard is monthly
 * or quarterly (3 months). Flexible follows PayFlow's Flexi price (days of
 * the week x hours per day) for a start date to an end date, billed for the
 * whole months it covers (a part month counts as one). Every link
 * records the centre's Active Care batch, which is required when it has any.
 */
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import React, { useCallback, useEffect, useState } from 'react'

import { createActiveCareLink, fetchUser, getProgramBatches } from 'api'
import { AutoComplete } from 'app/components/generic/FormFields'
import { PaymentLinkPanel } from 'app/pages/Apartment/shared/PaymentLinkCopy'
import { batchTimingsShortLabel } from 'app/pages/Class/Inventory/components/ModalsAndActions/components/AddProgramItemToCartModal/components/ParentChildForm/batchTimings'
import * as SeatStyles from 'app/pages/Class/Inventory/components/ModalsAndActions/components/AddProgramItemToCartModal/components/ParentChildForm/styles'

import { MonthPicker } from './MonthPicker'

import {
  ACTIVE_CARE_FLEXI_DROP_IN_PROGRAM,
  ACTIVE_CARE_LINK_PLANS,
  batchTypesProgramFlow,
  DROP_IN_DURATIONS,
  DAY_NAMES,
  SEAT_ICON,
  SEATS_FULL_ICON,
  FLEXIBLE_HOURS_PER_DAY,
  STANDARD_PACKAGES
} from '../../constants'

const GST_RATE = 0.18
const WEEKS_PER_MONTH = 4

const TITLES = {
  [ACTIVE_CARE_LINK_PLANS.STANDARD]: 'Active Care – Standard link',
  [ACTIVE_CARE_LINK_PLANS.FLEXIBLE]: 'Flexible Active Care link',
  [ACTIVE_CARE_LINK_PLANS.DROP_IN]: 'Drop-in Care link'
}

// Same as PayFlow's calculateFlexiblePricing and the backend's
// flexible_rate_per_hour. Preview only: the backend sets the price.
const flexibleRatePerHour = (days, hours) => {
  if (days === 1) {
    if (hours === 1) return 500
    if (hours === 3) return 333
    if (hours === 6) return 250
    if (hours < 3) return 500 - (hours - 1) * 83.5
    return 333 - (hours - 3) * 27.7
  }
  if (days === 2) {
    if (hours === 2) return 250
    if (hours === 4) return 200
    if (hours === 8) return 125
    if (hours < 4) return 250 - (hours - 2) * 25
    return 200 - (hours - 4) * 18.75
  }
  if (days === 3) {
    if (hours === 2) return 175
    if (hours === 4) return 150
    if (hours === 8) return 125
    if (hours < 4) return 175 - (hours - 2) * 12.5
    return 150 - (hours - 4) * 6.25
  }
  if (hours === 2) return 175
  if (hours >= 4) return 125
  return 175 - (hours - 2) * 25
}

const MAX_FLEXIBLE_MONTHS = 12

// Whole months a Flexible date range is billed for, a part month counting as
// one (as the backend's months_covering): 11-25 Nov is 1, 11 Nov-11 Dec is 2.
export const monthsCovering = (start, end) => {
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  const months = (ey - sy) * 12 + em - sm + (ed >= sd ? 1 : 0)
  return Math.max(months, 1)
}

// The centre's batches a link of this plan can record, as the backend's
// active_care_batches_for: Flexible and Drop-in go in the 1–7 pm Flexi &
// Drop-in batch, Standard in the others. A centre with no Flexi & Drop-in
// batch offers its other batches to every plan.
export const batchesForPlan = (batches, plan) => {
  const isFlexiDropIn = (batch) =>
    batch.program?.name === ACTIVE_CARE_FLEXI_DROP_IN_PROGRAM
  const flexiDropIn = batches.filter(isFlexiDropIn)
  const others = batches.filter((batch) => !isFlexiDropIn(batch))
  if (plan === ACTIVE_CARE_LINK_PLANS.STANDARD) return others
  return flexiDropIn.length ? flexiDropIn : others
}

// Readable name for a batch: most Active Care batches have no name of their
// own, so build one like "Active Care – Whitefield (Manjushree Mahadev)".
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// "Active Care Flexi & Drop-in (1pm-7pm)" -> "Flexi & Drop-in": the
// timings are shown separately, and every batch here is Active Care.
const conciseTitle = (title) => {
  const withoutTimes = title.replace(/\s*\([^)]*\d\s*[ap]m[^)]*\)\s*$/i, '')
  const rest = withoutTimes.replace(/^Active Care\b[\s:–-]*/i, '')
  return rest || withoutTimes || title
}

// Same shape as the enrolment form's Batch picker, kept short so it fits:
// "Flexi & Drop-in (Manjushree Mahadev) – 1:00pm-7:00pm". Without timings
// the full programme name stays, so the 1-5 pm and 1-7 pm batches still
// read apart.
export const batchLabel = (batch) => {
  // Some batches carry their own id as the name; treat that as no name.
  const ownName = (batch.name || '').trim()
  const name = ownName && !UUID_PATTERN.test(ownName) ? ownName : ''
  const timing = batchTimingsShortLabel(batch)
  const subjects = (batch.course?.subjects || [])
    .map((item) => item.name)
    .filter(Boolean)
    .join(', ')
  const full = name || batch.program?.name || subjects || 'Active Care'
  const title = timing ? conciseTitle(full) : full
  const teacher =
    batch.teacher?.full_name ||
    [batch.teacher?.user?.first_name, batch.teacher?.user?.last_name]
      .filter(Boolean)
      .join(' ')
  return `${title}${teacher ? ` (${teacher})` : ''}${
    timing ? ` – ${timing}` : ''
  }`
}

const flexiblePretax = (days, hours) =>
  Math.round(days * hours * WEEKS_PER_MONTH * flexibleRatePerHour(days, hours))

// Outlined dropdown whose label always sits above a chosen value, so it
// never overlaps the selection.
const SelectField = ({ id, label, value, children, ...props }) => (
  <TextField
    id={id}
    select
    label={label}
    value={value}
    variant="outlined"
    size="small"
    fullWidth
    margin="dense"
    InputLabelProps={value !== '' ? { shrink: true } : undefined}
    {...props}
  >
    {children}
  </TextField>
)

const EMPTY_VISIT = { date: '', duration: '' }
const MAX_DROP_IN_VISITS = 3

const rupees = (amount) =>
  `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`

const errorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  error?.message ||
  'Could not create the link. Try again.'

const ActiveCareLinkModal = ({ open, plan, centre, onClose }) => {
  const isStandard = plan === ACTIVE_CARE_LINK_PLANS.STANDARD
  const isFlexible = plan === ACTIVE_CARE_LINK_PLANS.FLEXIBLE
  const isDropIn = plan === ACTIVE_CARE_LINK_PLANS.DROP_IN
  const [parent, setParent] = useState(null)
  const [studentUuid, setStudentUuid] = useState('')
  const [month, setMonth] = useState('')
  const [standardHours, setStandardHours] = useState('')
  const [standardMonths, setStandardMonths] = useState(1)
  const [batches, setBatches] = useState([])
  const [batchUuid, setBatchUuid] = useState('')
  const [flexiDays, setFlexiDays] = useState([])
  const [hoursPerDay, setHoursPerDay] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [notes, setNotes] = useState('')
  const [visits, setVisits] = useState([EMPTY_VISIT])
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [createdLink, setCreatedLink] = useState(null)

  useEffect(() => {
    if (!open) return
    setParent(null)
    setStudentUuid('')
    setMonth('')
    setStandardHours('')
    setStandardMonths(1)
    setBatchUuid('')
    setFlexiDays([])
    setHoursPerDay('')
    setStartDate('')
    setEndDate('')
    setNotes('')
    setVisits([EMPTY_VISIT])
    setError('')
    setCreatedLink(null)
  }, [open, plan])

  useEffect(() => {
    if (!open || !centre?.value) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await getProgramBatches({
          queries: {
            centre_uuid: centre.value,
            type: batchTypesProgramFlow.CARE
          }
        })
        const results = batchesForPlan(response?.data?.results || [], plan)
        if (cancelled) return
        setBatches(results)
        if (results.length === 1) setBatchUuid(results[0].uuid)
      } catch (err) {
        if (!cancelled) setBatches([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, centre?.value, plan])

  // Same parent picker as the enrolment form: type the 10-digit number and
  // pick "9405222555 (Charmi Agrawal)" from the list.
  const searchParent = useCallback(async (text) => {
    const digits = (text || '').replace(/\D/g, '')
    setError('')
    if (digits.length !== 10) return []
    try {
      const response = await fetchUser(
        { queries: { phone_number: digits } },
        'parent'
      )
      const results = response?.results || []
      if (results.length > 1) {
        setError(
          'More than one parent has this number. Raise it on the issue tracker.'
        )
        return []
      }
      return results.map((item) => ({
        ...item,
        value: item.user.uuid,
        name: `${item.user.phone_number || digits} (${item.user.first_name} ${
          item.user.last_name
        })`
      }))
    } catch (err) {
      setError(errorMessage(err))
      return []
    }
  }, [])

  const onParentChange = (name, selected) => {
    setParent(selected || null)
    setError('')
    const students = selected?.students || []
    setStudentUuid(students.length === 1 ? students[0].user.uuid : '')
  }

  let pretax
  let planReady = false
  let flexiMonthly
  let flexiMonths
  let flexiDateError = ''
  if (isStandard) {
    const monthly = STANDARD_PACKAGES.find(
      (item) => item.value === standardHours
    )?.pretax
    pretax = monthly ? monthly * standardMonths : undefined
    planReady = !!month && !!pretax
  } else if (isFlexible) {
    flexiMonthly =
      flexiDays.length && hoursPerDay
        ? flexiblePretax(flexiDays.length, hoursPerDay)
        : undefined
    if (startDate && endDate) {
      if (endDate < startDate) {
        flexiDateError = 'The end date must be on or after the start date.'
      } else {
        flexiMonths = monthsCovering(startDate, endDate)
        if (flexiMonths > MAX_FLEXIBLE_MONTHS) {
          flexiDateError = `One link can cover up to ${MAX_FLEXIBLE_MONTHS} months.`
        }
      }
    }
    if (flexiMonthly && flexiMonths && !flexiDateError) {
      pretax = flexiMonthly * flexiMonths
    }
    planReady = !!pretax
  } else if (isDropIn) {
    const prices = visits.map(
      (visit) =>
        DROP_IN_DURATIONS.find((item) => item.value === visit.duration)?.pretax
    )
    const dates = visits.map((visit) => visit.date)
    planReady =
      dates.every(Boolean) &&
      prices.every(Boolean) &&
      new Set(dates).size === dates.length
    pretax = planReady
      ? prices.reduce((sum, price) => sum + price, 0)
      : undefined
  }
  const gst = pretax ? Math.round(pretax * GST_RATE) : 0

  const canSubmit =
    planReady &&
    (!batches.length || !!batchUuid) &&
    !!parent &&
    !!studentUuid &&
    !!centre?.value &&
    !submitting &&
    !createdLink

  const onSubmit = async () => {
    setSubmitting(true)
    setError('')
    const details = isDropIn
      ? {
          visits: visits.map((visit) => ({
            visit_date: visit.date,
            duration: visit.duration
          }))
        }
      : isFlexible
      ? {
          flexi_days: [...flexiDays].sort(),
          hours_per_day: hoursPerDay,
          start_date: startDate,
          end_date: endDate,
          notes
        }
      : {
          start_month: `${month}-01`,
          hours_per_day: standardHours,
          months: standardMonths
        }
    try {
      const response = await createActiveCareLink({
        student: studentUuid,
        centre: centre.value,
        plan,
        batch: batchUuid || null,
        ...details
      })
      setCreatedLink(response?.data || response)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  const locked = !!createdLink
  const selectedBatch = batches.find((batch) => batch.uuid === batchUuid)

  const updateVisit = (index, field, value) =>
    setVisits((current) =>
      current.map((visit, i) =>
        i === index ? { ...visit, [field]: value } : visit
      )
    )
  const dates = visits.map((visit) => visit.date).filter(Boolean)
  const hasRepeatedDate = new Set(dates).size !== dates.length

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{TITLES[plan]}</DialogTitle>
      <DialogContent>
        {!centre?.value && (
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            Pick a centre at the top of the page first.
          </Alert>
        )}
        <div style={{ marginTop: 8, marginBottom: 4 }}>
          <AutoComplete
            label="Parent"
            placeholder="Enter parent mobile number"
            size="small"
            minCharactersToSearch={10}
            async
            fetchOnMount={false}
            searchWithEmptyValue={false}
            clearOnBlur={false}
            throttleMs={300}
            fetchValues={searchParent}
            onChange={onParentChange}
            emptyValue={!parent}
            disabled={locked}
            noResultsPlaceholder="No parent found with this number."
          />
        </div>
        <SelectField
          id="active-care-child"
          label="Child"
          value={studentUuid}
          onChange={(event) => setStudentUuid(event.target.value)}
          disabled={!parent || locked}
        >
          {(parent?.students || []).map((student) => (
            <MenuItem key={student.user.uuid} value={student.user.uuid}>
              {student.user.first_name} {student.user.last_name}
            </MenuItem>
          ))}
        </SelectField>

        {batches.length ? (
          <SelectField
            id="active-care-batch"
            label="Batch"
            required
            value={batchUuid}
            onChange={(event) => setBatchUuid(event.target.value)}
            disabled={locked}
            FormHelperTextProps={{ style: { marginLeft: 0 } }}
            helperText={
              selectedBatch && selectedBatch.vacancy != null ? (
                <SeatStyles.BatchHelperText as="span" style={{ marginLeft: 0 }}>
                  <SeatStyles.SeatIcon
                    src={
                      selectedBatch.vacancy === 0 ? SEATS_FULL_ICON : SEAT_ICON
                    }
                    alt=""
                  />
                  <SeatStyles.SeatsSpan full={selectedBatch.vacancy === 0}>
                    {selectedBatch.enrolment_capacity - selectedBatch.vacancy}/
                    {selectedBatch.enrolment_capacity} seats reserved
                  </SeatStyles.SeatsSpan>
                </SeatStyles.BatchHelperText>
              ) : undefined
            }
          >
            {batches.map((batch) => (
              <MenuItem key={batch.uuid} value={batch.uuid}>
                {batchLabel(batch)}
              </MenuItem>
            ))}
          </SelectField>
        ) : (
          centre?.value && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              This centre has no Active Care batch set up, so none is needed.
            </Typography>
          )
        )}

        {isStandard && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={standardMonths}
            onChange={(event, value) => value && setStandardMonths(value)}
            style={{ marginTop: 12 }}
          >
            <ToggleButton value={1} disabled={locked}>
              Monthly
            </ToggleButton>
            <ToggleButton value={3} disabled={locked}>
              Quarterly (3 months)
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {isStandard && (
          <MonthPicker
            id="active-care-month"
            label={standardMonths > 1 && isStandard ? 'Start month' : 'Month'}
            value={month}
            onChange={setMonth}
            disabled={locked}
          />
        )}

        {isStandard && (
          <>
            <Typography variant="subtitle2" style={{ marginTop: 12 }}>
              Package
            </Typography>
            <RadioGroup
              value={standardHours}
              onChange={(event) => setStandardHours(Number(event.target.value))}
            >
              {STANDARD_PACKAGES.map((item) => (
                <FormControlLabel
                  key={item.value}
                  value={item.value}
                  control={<Radio color="primary" size="small" />}
                  disabled={locked}
                  label={
                    standardMonths > 1
                      ? `${item.label} — ${rupees(
                          item.pretax * standardMonths
                        )} for ${standardMonths} months + GST (${rupees(
                          item.pretax
                        )} a month)`
                      : `${item.label} — ${rupees(item.pretax)} a month + GST`
                  }
                />
              ))}
            </RadioGroup>
          </>
        )}

        {isFlexible && (
          <>
            <Typography variant="subtitle2" style={{ marginTop: 12 }}>
              Days of the week
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {DAY_NAMES.map((day, index) => {
                const picked = flexiDays.includes(index)
                return (
                  <Chip
                    key={day}
                    label={day}
                    clickable={!locked}
                    color={picked ? 'primary' : 'default'}
                    variant={picked ? 'default' : 'outlined'}
                    aria-pressed={picked}
                    onClick={
                      locked
                        ? undefined
                        : () =>
                            setFlexiDays((current) =>
                              picked
                                ? current.filter((d) => d !== index)
                                : [...current, index]
                            )
                    }
                  />
                )
              })}
            </div>
            <SelectField
              id="active-care-hours"
              label="Hours per day"
              value={hoursPerDay}
              onChange={(event) => setHoursPerDay(event.target.value)}
              disabled={locked}
            >
              {FLEXIBLE_HOURS_PER_DAY.map((hours) => (
                <MenuItem key={hours} value={hours}>
                  {hours}
                </MenuItem>
              ))}
            </SelectField>
            <div style={{ display: 'flex', gap: 8 }}>
              <TextField
                id="active-care-start-date"
                label="Start date"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                variant="outlined"
                size="small"
                margin="dense"
                disabled={locked}
                InputLabelProps={{ shrink: true }}
                style={{ flex: 1 }}
              />
              <TextField
                id="active-care-end-date"
                label="End date"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                variant="outlined"
                size="small"
                margin="dense"
                disabled={locked}
                inputProps={{ min: startDate || undefined }}
                InputLabelProps={{ shrink: true }}
                style={{ flex: 1 }}
              />
            </div>
            {flexiDateError ? (
              <Typography variant="body2" color="error">
                {flexiDateError}
              </Typography>
            ) : flexiMonths ? (
              <Typography variant="body2" color="textSecondary">
                Billed as {flexiMonths} month{flexiMonths > 1 ? 's' : ''} (a
                part month counts as a full month)
              </Typography>
            ) : null}
            <TextField
              id="active-care-notes"
              label="Notes / special instructions"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              margin="dense"
              disabled={locked}
            />
            {flexiMonthly && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                {flexiDays.length * hoursPerDay * WEEKS_PER_MONTH} hours/month @{' '}
                {rupees(
                  Math.round(flexibleRatePerHour(flexiDays.length, hoursPerDay))
                )}
                /hour = {rupees(flexiMonthly)} a month
              </Typography>
            )}
          </>
        )}

        {isDropIn && (
          <>
            <Typography variant="subtitle2" style={{ marginTop: 12 }}>
              Visits
            </Typography>
            {visits.map((visit, index) => (
              <div
                key={index}
                style={{ display: 'flex', gap: 8, alignItems: 'center' }}
              >
                <TextField
                  id={`active-care-visit-date-${index}`}
                  label={`Visit ${index + 1} date`}
                  type="date"
                  value={visit.date}
                  onChange={(event) =>
                    updateVisit(index, 'date', event.target.value)
                  }
                  variant="outlined"
                  size="small"
                  margin="dense"
                  disabled={locked}
                  InputLabelProps={{ shrink: true }}
                  style={{ flex: 1 }}
                />
                <SelectField
                  id={`active-care-visit-duration-${index}`}
                  label="How long"
                  value={visit.duration}
                  onChange={(event) =>
                    updateVisit(index, 'duration', event.target.value)
                  }
                  disabled={locked}
                  fullWidth={false}
                  style={{ flex: 1 }}
                >
                  {DROP_IN_DURATIONS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label} — {rupees(item.pretax)} + GST
                    </MenuItem>
                  ))}
                </SelectField>
                {visits.length > 1 && !locked && (
                  <Button
                    size="small"
                    onClick={() =>
                      setVisits((current) =>
                        current.filter((_, i) => i !== index)
                      )
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            {hasRepeatedDate && (
              <Typography variant="body2" color="error">
                Each visit needs a different date.
              </Typography>
            )}
            {!locked && visits.length < MAX_DROP_IN_VISITS && (
              <Button
                size="small"
                color="primary"
                onClick={() =>
                  setVisits((current) => [...current, EMPTY_VISIT])
                }
                style={{ marginTop: 4 }}
              >
                + Add another date
              </Button>
            )}
          </>
        )}

        {pretax && (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Parent pays {rupees(pretax + gst)} ({rupees(pretax)} + {rupees(gst)}{' '}
            GST)
          </Typography>
        )}
        {createdLink && (
          <PaymentLinkPanel
            url={createdLink.payment_link}
            amountLabel={rupees(createdLink.total_amount)}
          />
        )}
        {error && (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{createdLink ? 'Done' : 'Cancel'}</Button>
        {!createdLink && (
          <Button
            color="primary"
            variant="contained"
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitting ? 'Creating…' : 'Create payment link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export { ActiveCareLinkModal }
